import React, { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Route, Outlet } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SnackbarProvider } from 'notistack';
import { NotificationProvider } from './hooks/useNotification';
import LoadingFallback from './components/LoadingFallback';
import ErrorBoundary from './components/ErrorBoundary';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { reloadTranslations } from './i18n/i18n'; // Import the reloadTranslations function

// Import i18n configuration
import './i18n/i18n';

// Lazy load components with explicit chunk names and preload hints
const Layout = lazy(() => import(/* webpackChunkName: "layout" */ './components/Layout'));
const PublicLayout = lazy(() => import(/* webpackChunkName: "public-layout" */ './components/PublicLayout'));
const AuthCallback = lazy(() => import(/* webpackChunkName: "auth" */ './components/AuthCallback'));
const Dashboard = lazy(() => 
  import(/* webpackChunkName: "dashboard" *//* webpackPrefetch: true */ './components/Dashboard')
);
const Login = lazy(() => 
  import(/* webpackChunkName: "auth" *//* webpackPrefetch: true */ './components/Login')
);
const Settings = lazy(() => import(/* webpackChunkName: "settings" */ './components/Settings'));
const RecycleBin = lazy(() => import(/* webpackChunkName: "recycle-bin" */ './components/RecycleBin'));
const AutomationRulesPage = lazy(() => import(/* webpackChunkName: "automation-rules" */ './components/AutomationRulesPage'));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "privacy-policy" */ './components/PrivacyPolicy'));

// Context providers with explicit chunk names
const CurrencyProvider = lazy(() => 
  import(/* webpackChunkName: "context" */ './context/CurrencyContext')
    .then(module => ({ default: module.CurrencyProvider }))
);

// Create rtl cache
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// Create ltr cache
const cacheLtr = createCache({
  key: 'mui',
});

const getDirection = (language) => {
  return language === 'he' ? 'rtl' : 'ltr';
};

// Light theme configuration
const lightTheme = {
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff'
    }
  }
};

// Dark theme configuration
const darkTheme = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e'
    }
  }
};

const createAppRouter = () => {
  const PrivateRoute = ({ children }) => {
    const { currentUser } = useAuth();
    return currentUser ? children : <Navigate to="/login" />;
  };

  const withSuspense = (Component, loadingMessage) => (
    <Suspense fallback={<LoadingFallback message={loadingMessage} />}>
      <ErrorBoundary>
        <Component />
      </ErrorBoundary>
    </Suspense>
  );

  return createBrowserRouter([
    {
      element: <Outlet />,
      children: [
        {
          path: '/',
          element: <PrivateRoute><Layout /></PrivateRoute>,
          children: [
            {
              index: true,
              element: withSuspense(Dashboard, 'Loading dashboard...')
            },
            {
              path: 'settings',
              element: withSuspense(Settings, 'Loading settings...')
            },
            {
              path: 'recycle-bin',
              element: withSuspense(RecycleBin, 'Loading recycle bin...')
            },
            {
              path: 'automation-rules',
              element: withSuspense(AutomationRulesPage, 'Loading automation rules...')
            },
            {
              path: 'auth/callback',
              element: <AuthCallback />
            }
          ]
        },
        {
          path: '/login',
          element: withSuspense(Login, 'Loading login page...')
        },
        {
          path: '/privacy-policy',
          element: withSuspense(PrivacyPolicy, 'Loading privacy policy...')
        }
      ]
    }
  ], {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  });
};

function App() {
  // Reload translations when the app starts
  useEffect(() => {
    reloadTranslations();
  }, []);

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ThemeProvider>
          <AppWithLanguageAndTheme />
        </ThemeProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

function AppWithLanguageAndTheme() {
  const { currentLanguage } = useLanguage();
  const { mode } = useTheme();
  const direction = getDirection(currentLanguage);
  const cache = direction === 'rtl' ? cacheRtl : cacheLtr;
  
  // Create theme with direction and mode
  const theme = createTheme({
    direction: direction,
    ...(mode === 'light' ? lightTheme : darkTheme)
  });

  return (
    <CacheProvider value={cache}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Suspense fallback={<LoadingFallback message="Loading app..." />}>
            <SnackbarProvider 
              maxSnack={3} 
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              autoHideDuration={3000}
            >
              <NotificationProvider>
                <CurrencyProvider>
                  <RouterProvider router={createAppRouter()} />
                </CurrencyProvider>
              </NotificationProvider>
            </SnackbarProvider>
          </Suspense>
        </AuthProvider>
      </MuiThemeProvider>
    </CacheProvider>
  );
}

export default App;
